import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../services/auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface PlanLimits {
  'project.max': number;
  'conversation.max_per_project': number;
  'conversation.max_unfiled': number;
  'conversation.context_max_tokens': number;
  'message.max_chars': number;
}

export interface ContextUsage {
  /** Clamped to `limit`; an assistant reply can push actual usage past the cap. */
  used: number;
  limit: number;
}

const UNLIMITED = -1;

/**
 * The plan's limits and the conversation's context usage, for the composer.
 *
 * Presentation only. The server gates in `chatLimits` are the enforcement
 * boundary — this exists so the user sees the wall coming instead of hitting a
 * 403 with no warning, which is the main cost of a hard stop.
 */
export function usePlanLimits(sessionId?: string | null) {
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [usage, setUsage] = useState<ContextUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiFetch(`${API_BASE}/api/tenant/entitlements`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setLimits(data.limits as PlanLimits);
      } catch {
        // A limits lookup failing must not break the composer; the server still
        // enforces, and the counter simply does not appear.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshUsage = useCallback(async () => {
    if (!sessionId) { setUsage(null); return; }
    try {
      const response = await apiFetch(
        `${API_BASE}/api/tenant/context-usage?sessionId=${encodeURIComponent(sessionId)}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      setUsage({ used: data.used, limit: data.limit });
    } catch {
      // Same reasoning as above.
    }
  }, [sessionId]);

  useEffect(() => { void refreshUsage(); }, [refreshUsage]);

  const maxChars = limits?.['message.max_chars'] ?? 0;

  return {
    limits,
    usage,
    refreshUsage,
    /** `undefined` rather than 0 so an unresolved limit does not lock the textarea. */
    maxChars: maxChars > 0 ? maxChars : undefined,
    contextFull: usage !== null && usage.limit !== UNLIMITED && usage.used >= usage.limit,
  };
}
